import { Button, Divider, Input } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import * as React from 'react';
import { NextStepButton, SaveButtonsGroup } from '../../containers/save-buttons/SaveButtonsGroup';
import ContentConstants from '../../contentConstants';
import { AudiencePropertiesUnderValidation, IAction } from '../../models';
import { ActionCreators, AudienceDetailsState } from '../../store/AudienceDetailsStore';
import * as EmailTemplateActionCreators from '../../store/EmailTemplateSlice';
import { IEmailTemplateState } from '../../store/EmailTemplateSlice';
import { ValidationErrorHelper } from '../../utilities/ValidationErrorHelper';
import { AudienceMode } from '../audiences/AudienceMode';
import { CardLayout, Col3Col9, Section, WarningModal } from '../common';
import * as AudienceValidation from './AudienceValidation';
import { OpenCloseDateRange } from './OpenCloseDateRange';
import { Timezone } from './Timezone';

export type AudienceDetailsBasicProps = {
	surveyId: number;
	audienceId?: number;
	mode: AudienceMode;
} & typeof ActionCreators &
	AudienceDetailsState &
	typeof EmailTemplateActionCreators &
	IEmailTemplateState;

export class AudienceDetailsBasic extends React.Component<AudienceDetailsBasicProps> {
	constructor(props: AudienceDetailsBasicProps) {
		super(props);

		this.discardUnsavedChanges = this.discardUnsavedChanges.bind(this);
		this.goToNextStep = this.goToNextStep.bind(this);
		this.handleNameChange = this.handleNameChange.bind(this);
	}

	public componentDidMount() {
		if (!this.props.audienceId) {
			this.props.InitState();
		}
	}

	public componentWillUnmount() {
		this.props.InitState();
	}

	public render() {
		const { audience, loading, reminderEmails, mode, surveyId } = this.props;

		const validationErrorHelper = this.getValidationHelper(this.props);
		const title = !audience.audienceId ? ContentConstants.AUDIENCEDETAILS_ADD_PAGE_TITLE : ContentConstants.AUDIENCEDETAILS_EDIT_PAGE_TITLE;
		return (
			<>
				<CardLayout title={title} loading={loading}>
					<Section sectionHeader={ContentConstants.SECTIONHEADERS_AUDIENCE_BASICS}>
						<Col3Col9 label={ContentConstants.AUDIENCEDETAILS_NAME_SECTION_LABEL} clearHeight='40px' className='tws-survey-audience-name'>
							<Input
								id='audienceName'
								value={audience.audienceName}
								maxLength={50}
								onChange={this.handleNameChange}
								placeholder={ContentConstants.INPUT_PLACEHOLDER_ENTER_AUDIENCE_NAME}
								error={!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.AudienceName)}
								message={validationErrorHelper.Errors(AudiencePropertiesUnderValidation.AudienceName)}
							/>
						</Col3Col9>
						<OpenCloseDateRange
							mode={mode}
							openOn={audience.openOn}
							closeOn={audience.closeOn}
							openImmediately={audience.openImmediately}
							validationError={_.cloneDeep(audience.validationErrors)}
							reminderEmails={reminderEmails}
							onChange={this.props.HandleOpenCloseDatesChange}
						/>
						<Col3Col9 label={ContentConstants.AUDIENCE_TIMEZONE_SECTION_LABEL}>
							<Timezone />
						</Col3Col9>

						<Divider type='dashed' />
						<SaveButtonsGroup>
							<Button id='CancelButton' color='secondary' size='lg' onClick={() => this.props.GoToList(audience.action, surveyId, mode)}>
								Cancel
							</Button>
							<NextStepButton
								disabled={this.props.saveInProgress}
								onClick={this.goToNextStep}
								text={ContentConstants.BUTTONS_SAVE_AND_DISTRIBUTION}
							/>
						</SaveButtonsGroup>
					</Section>
				</CardLayout>

				{this.props.unsavedChangesWarningModalOpen && (
					<WarningModal
						isOpen={this.props.unsavedChangesWarningModalOpen}
						contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
						onSubmit={this.discardUnsavedChanges}
						onClose={() => this.props.ToggleUnsavedWarningModal(false)}
					/>
				)}
			</>
		);
	}

	public getValidationHelper(props: AudienceDetailsBasicProps) {
		return new ValidationErrorHelper<AudiencePropertiesUnderValidation>(props.audience.validationErrors);
	}

	public handleNameChange(e: any) {
		const { audience } = this.props;
		this.props.HandleAudienceChange({ ...audience, audienceName: e.target.value });
	}

	// Save audience name and openOn/closeOn, then navigate to distribution options step
	public goToNextStep() {
		const { audience, mode, surveyId } = this.props;

		const audienceValidationErrors = AudienceValidation.validAudienceFields(
			[
				AudiencePropertiesUnderValidation.AudienceName,
				AudiencePropertiesUnderValidation.OpenOn,
				AudiencePropertiesUnderValidation.CloseOn,
				AudiencePropertiesUnderValidation.OpenCloseDates,
			],
			audience,
		);
		this.props.ValidationErrors(audienceValidationErrors);
		if (!audienceValidationErrors) {
			this.props.SaveAudienceBasics(audience, surveyId, mode);
		}
	}

	public discardUnsavedChanges() {
		const { mode, surveyId } = this.props;
		this.props.ToggleUnsavedWarningModal(false);
		this.props.GoToList(IAction.None, surveyId, mode);
	}
}
